import React from "react";
import { FaCalendarPlus } from "react-icons/fa";
import Spinner from "../../../components/Spinner";
import Modal from "../../../components/Modal";
import type { IconComponent, OfficialSchoolHoliday } from "../types";

const CalendarPlusIcon = FaCalendarPlus as IconComponent;

interface OfficialHolidayModalProps {
  t: any;
  i18n: any;
  officialHolidayModalOpen: boolean;
  setOfficialHolidayModalOpen: (open: boolean) => void;
  selectedSchoolYearApiLabel: string;
  officialHolidaysError: string;
  officialHolidaysLoading: boolean;
  officialHolidays: OfficialSchoolHoliday[];
  selectedOfficialHolidayIds: string[];
  toggleOfficialHolidaySelection: (holidayId: string) => void;
  saveOfficialHolidays: () => void;
  officialHolidaysSaving: boolean;
  formatDateOnly: (value: any, locale?: string) => string;
}

const OfficialHolidayModal = ({
  t,
  i18n,
  officialHolidayModalOpen,
  setOfficialHolidayModalOpen,
  selectedSchoolYearApiLabel,
  officialHolidaysError,
  officialHolidaysLoading,
  officialHolidays,
  selectedOfficialHolidayIds,
  toggleOfficialHolidaySelection,
  saveOfficialHolidays,
  officialHolidaysSaving,
  formatDateOnly,
}: OfficialHolidayModalProps) => (
  <Modal
    show={officialHolidayModalOpen}
    type="manage"
    icon={<CalendarPlusIcon className="text-indigo-500 text-5xl" />}
    title={t("schoolCalendar.officialHolidays.title", { defaultValue: "Vakantiedagen toevoegen" })}
    onClose={() => setOfficialHolidayModalOpen(false)}
    hideDefaultActions
    dialogClassName="!w-[min(92vw,42rem)] !max-w-none text-left"
  >
    <div className="space-y-4 text-left">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {t("schoolCalendar.officialHolidays.intro", {
          schoolYear: selectedSchoolYearApiLabel,
          defaultValue:
            "Selecteer welke officiële schoolvakanties van de Rijksoverheid je wilt toevoegen aan {{schoolYear}}.",
        })}
      </p>

      {officialHolidaysError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-100">
          {officialHolidaysError}
        </div>
      )}

      {officialHolidaysLoading ? (
        <div className="px-6 py-10">
          <Spinner />
        </div>
      ) : (
        <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
          {officialHolidays.map((holiday) => (
            <label
              key={holiday.id}
              className="flex cursor-pointer gap-3 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm shadow-sm hover:bg-indigo-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
            >
              <input
                type="checkbox"
                checked={selectedOfficialHolidayIds.includes(holiday.id)}
                onChange={() => toggleOfficialHolidaySelection(holiday.id)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-gray-900 dark:text-gray-100">{holiday.title}</span>
                <span className="mt-1 block text-xs text-gray-600 dark:text-gray-300">
                  {formatDateOnly(holiday.startDate, i18n.language)}
                  {holiday.endDate !== holiday.startDate &&
                    ` - ${formatDateOnly(holiday.endDate, i18n.language)}`}
                  {` · ${holiday.region}`}
                </span>
                {holiday.notice && (
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{holiday.notice}</span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={() => setOfficialHolidayModalOpen(false)}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          {t("common.cancel", { defaultValue: "Annuleren" })}
        </button>

        <button
          type="button"
          onClick={saveOfficialHolidays}
          disabled={officialHolidaysLoading || officialHolidaysSaving || selectedOfficialHolidayIds.length === 0}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {officialHolidaysSaving
            ? t("common.loading", { defaultValue: "Laden..." })
            : t("schoolCalendar.officialHolidays.save", {
                defaultValue: "Geselecteerde vakanties toevoegen",
              })}
        </button>
      </div>
    </div>
  </Modal>
);

export default OfficialHolidayModal;
